import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, Save, Pencil, X, UserCog, Target, Percent, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  getSupervisors, saveSupervisor,
  getSupervisorCriteria, saveSupervisorCriterion, deleteSupervisorCriterion,
} from '@/services/payrollApi';

const TYPE_LABELS = {
  staff_productivity: 'Staff productivity (auto)',
  opening_stock_backup: 'One-day stock backup (data pending)',
  absenteeism: 'Absenteeism (data pending)',
  manual: 'Manual (data pending)',
};

const EMPTY_CRIT = { criterion_id: null, name: '', type: 'manual', weightage_pct: 0, threshold_pct: 0, active: true };

const SupervisorConfig = () => {
  const [isAdmin, setIsAdmin] = useState(false);

  // Assignments
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingOp, setSavingOp] = useState(null);

  // Criteria
  const [criteria, setCriteria] = useState([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [critForm, setCritForm] = useState(EMPTY_CRIT);
  const [savingCrit, setSavingCrit] = useState(false);

  const loadSupervisors = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getSupervisors();
      setRows(d.supervisors || []);
      setIsAdmin(!!d.is_admin);
      const e = {};
      (d.supervisors || []).forEach((r) => {
        e[r.operation_code] = (r.supervisors || []).map((s) => ({
          staffId: s.supervisor_staff_id || '',
          rate: String(s.rate ?? 0),
          dailyIncentive: String(s.daily_incentive ?? 0),
        }));
      });
      setEdits(e);
    } catch (err) { toast.error('Failed to load supervisors'); }
    finally { setLoading(false); }
  }, []);

  const loadCriteria = useCallback(async () => {
    try {
      const d = await getSupervisorCriteria();
      setCriteria(d.criteria || []);
      setTotalWeight(d.total_weightage_pct ?? 0);
    } catch (err) { toast.error('Failed to load criteria'); }
  }, []);

  useEffect(() => { loadSupervisors(); loadCriteria(); }, [loadSupervisors, loadCriteria]);

  const setSupField = (code, idx, field, val) => setEdits((p) => {
    const list = [...(p[code] || [])];
    list[idx] = { ...list[idx], [field]: val };
    return { ...p, [code]: list };
  });
  const addSup = (code) => setEdits((p) => ({
    ...p, [code]: [...(p[code] || []), { staffId: '', rate: '0', dailyIncentive: '0' }],
  }));
  const removeSup = (code, idx) => setEdits((p) => {
    const list = [...(p[code] || [])];
    list.splice(idx, 1);
    return { ...p, [code]: list };
  });

  const handleSaveOp = async (row) => {
    const list = edits[row.operation_code] || [];
    const cleaned = [];
    const seen = new Set();
    for (const s of list) {
      const staffId = (s.staffId || '').trim();
      if (!staffId) continue;
      const rate = parseFloat(s.rate);
      const inc = parseFloat(s.dailyIncentive);
      if (isNaN(rate) || rate < 0) { toast.error('Enter a valid rate for each supervisor'); return; }
      if (isNaN(inc) || inc < 0) { toast.error('Enter a valid daily incentive for each supervisor'); return; }
      if (seen.has(staffId.toUpperCase())) { toast.error(`Duplicate supervisor "${staffId}" in ${row.operation}`); return; }
      seen.add(staffId.toUpperCase());
      cleaned.push({ staffId, rate, dailyIncentive: inc });
    }
    setSavingOp(row.operation_code);
    try {
      await saveSupervisor({ operationCode: row.operation_code, supervisors: cleaned });
      toast.success(`Saved supervisors for ${row.operation}`);
      await loadSupervisors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSavingOp(null); }
  };

  const startEditCrit = (c) => setCritForm({
    criterion_id: c.criterion_id, name: c.name || '', type: c.type || 'manual',
    weightage_pct: c.weightage_pct ?? 0, threshold_pct: c.threshold_pct ?? 0, active: c.active !== false,
  });
  const resetCrit = () => setCritForm(EMPTY_CRIT);

  const handleSaveCrit = async () => {
    if (!critForm.name.trim()) { toast.error('Criterion name is required'); return; }
    const w = parseFloat(critForm.weightage_pct);
    if (isNaN(w) || w < 0 || w > 100) { toast.error('Weightage must be 0–100'); return; }
    const t = parseFloat(critForm.threshold_pct);
    if (isNaN(t) || t < 0) { toast.error('Enter a valid threshold %'); return; }
    setSavingCrit(true);
    try {
      await saveSupervisorCriterion({
        criterion_id: critForm.criterion_id || undefined,
        name: critForm.name.trim(), type: critForm.type,
        weightage_pct: w, threshold_pct: t, active: !!critForm.active,
      });
      toast.success(critForm.criterion_id ? 'Criterion updated' : 'Criterion added');
      resetCrit();
      await loadCriteria();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSavingCrit(false); }
  };

  const handleDeleteCrit = async (c) => {
    if (!window.confirm(`Delete criterion "${c.name}"?`)) return;
    try {
      await deleteSupervisorCriterion(c.criterion_id);
      toast.success('Criterion deleted');
      if (critForm.criterion_id === c.criterion_id) resetCrit();
      await loadCriteria();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="supervisor-config">
      {/* Supervisor assignment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><UserCog className="w-5 h-5" />Operation Supervisors</CardTitle>
          <CardDescription>
            Assign one or more supervisors to each operation, each with their own per-piece rate and
            daily incentive pool. Every assigned supervisor is paid on the operation&apos;s FULL output
            (all staff) × their own rate. The same supervisor can be assigned to multiple operations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-lg" data-testid="supervisors-table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A1F2E] text-white text-left">
                  <th className="px-3 py-2 min-w-[130px]">Module</th>
                  <th className="px-3 py-2 min-w-[170px]">Operation</th>
                  <th className="px-3 py-2 min-w-[380px]">Supervisors <span className="font-normal text-white/60">(staff ID · rate/pc · daily incentive)</span></th>
                  {isAdmin && <th className="px-3 py-2 text-center min-w-[90px]">Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={isAdmin ? 4 : 3} className="py-6 text-center text-[#5A5F6E]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                ) : rows.map((r) => {
                  const list = edits[r.operation_code] || [];
                  return (
                    <tr key={r.operation_code} className="border-t align-top hover:bg-[#F5F1E8]" data-testid={`supervisor-row-${r.operation_code}`}>
                      <td className="px-3 py-3 text-[#5A5F6E]">{r.module}</td>
                      <td className="px-3 py-3 font-medium">{r.operation}</td>
                      <td className="px-3 py-3">
                        <div className="space-y-2">
                          {list.length === 0 && (
                            <div className="text-xs text-[#5A5F6E] italic" data-testid={`supervisor-none-${r.operation_code}`}>No supervisor assigned</div>
                          )}
                          {list.map((s, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2" data-testid={`supervisor-entry-${r.operation_code}-${idx}`}>
                              <Input value={s.staffId || ''} disabled={!isAdmin}
                                onChange={(ev) => setSupField(r.operation_code, idx, 'staffId', ev.target.value)}
                                placeholder="Staff ID e.g. S0123" className="h-8 w-40"
                                data-testid={`supervisor-staff-${r.operation_code}-${idx}`} />
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-[#5A5F6E]">₹</span>
                                <Input type="number" step="0.01" min="0" value={s.rate ?? '0'} disabled={!isAdmin}
                                  onChange={(ev) => setSupField(r.operation_code, idx, 'rate', ev.target.value)}
                                  className="h-8 w-24 text-right" placeholder="rate/pc"
                                  data-testid={`supervisor-rate-${r.operation_code}-${idx}`} />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-[#5A5F6E]">incentive</span>
                                <Input type="number" step="1" min="0" value={s.dailyIncentive ?? '0'} disabled={!isAdmin}
                                  onChange={(ev) => setSupField(r.operation_code, idx, 'dailyIncentive', ev.target.value)}
                                  className="h-8 w-28 text-right" placeholder="daily pool"
                                  data-testid={`supervisor-incentive-${r.operation_code}-${idx}`} />
                              </div>
                              {isAdmin && (
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                                  onClick={() => removeSup(r.operation_code, idx)}
                                  data-testid={`supervisor-remove-${r.operation_code}-${idx}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          {isAdmin && (
                            <Button size="sm" variant="outline" className="h-8"
                              onClick={() => addSup(r.operation_code)}
                              data-testid={`supervisor-add-${r.operation_code}`}>
                              <Plus className="w-4 h-4 mr-1" />Add supervisor
                            </Button>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-3 text-center">
                          <Button size="sm" onClick={() => handleSaveOp(r)} disabled={savingOp === r.operation_code}
                            className="bg-[#2D5F4F] hover:bg-[#234A3D] h-8" data-testid={`supervisor-save-${r.operation_code}`}>
                            {savingOp === r.operation_code ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Incentive criteria */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5" />Supervisor Incentive Criteria</CardTitle>
          <CardDescription>
            Each criterion carries a weightage %. A supervisor&apos;s daily incentive = daily pool ×
            (Σ weightage × how much of that criterion was met). &quot;Staff productivity&quot; is computed
            automatically; &quot;One-day stock backup&quot; and &quot;Absenteeism&quot; show once their data source is wired.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={Math.abs(totalWeight - 100) < 0.01 ? 'default' : 'secondary'} data-testid="crit-total-weight">
              Total weightage: {totalWeight}%
            </Badge>
            {Math.abs(totalWeight - 100) > 0.01 && (
              <span className="flex items-center gap-1 text-xs text-[#B45309]">
                <AlertTriangle className="w-3.5 h-3.5" /> Weightages don&apos;t sum to 100%
              </span>
            )}
          </div>

          <div className="overflow-auto border rounded-lg" data-testid="criteria-table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A1F2E] text-white text-left">
                  <th className="px-3 py-2 min-w-[200px]">Criterion</th>
                  <th className="px-3 py-2 min-w-[200px]">Type</th>
                  <th className="px-3 py-2 text-right">Weightage</th>
                  <th className="px-3 py-2 text-right">Threshold</th>
                  <th className="px-3 py-2 text-center">Active</th>
                  {isAdmin && <th className="px-3 py-2 text-center min-w-[100px]">Action</th>}
                </tr>
              </thead>
              <tbody>
                {criteria.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="py-6 text-center text-[#5A5F6E]">No criteria yet.</td></tr>
                ) : criteria.map((c) => (
                  <tr key={c.criterion_id} className="border-t hover:bg-[#F5F1E8]" data-testid={`criterion-row-${c.criterion_id}`}>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 text-[#5A5F6E]">{TYPE_LABELS[c.type] || c.type}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#2D5F4F]">{c.weightage_pct}%</td>
                    <td className="px-3 py-2 text-right text-[#5A5F6E]">{c.threshold_pct || 0}%</td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant={c.active !== false ? 'default' : 'secondary'}>{c.active !== false ? 'Active' : 'Off'}</Badge>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditCrit(c)} data-testid={`criterion-edit-${c.criterion_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => handleDeleteCrit(c)} data-testid={`criterion-delete-${c.criterion_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isAdmin && (
            <div className="border rounded-lg p-4 bg-[#FAFAF7]" data-testid="criterion-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm">{critForm.criterion_id ? 'Edit criterion' : 'Add a criterion'}</h4>
                {critForm.criterion_id && (
                  <Button size="sm" variant="ghost" onClick={resetCrit} data-testid="criterion-form-cancel"><X className="w-4 h-4 mr-1" />Cancel edit</Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div className="col-span-2">
                  <Label className="text-xs">Name</Label>
                  <Input value={critForm.name} onChange={(e) => setCritForm({ ...critForm, name: e.target.value })} placeholder="e.g. Housekeeping" className="mt-1" data-testid="criterion-name" />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={critForm.type} onValueChange={(v) => setCritForm({ ...critForm, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="criterion-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Weightage %</Label>
                  <Input type="number" step="1" min="0" max="100" value={critForm.weightage_pct} onChange={(e) => setCritForm({ ...critForm, weightage_pct: e.target.value })} className="mt-1" data-testid="criterion-weightage" />
                </div>
                <div>
                  <Label className="text-xs">Threshold %</Label>
                  <Input type="number" step="1" min="0" value={critForm.threshold_pct} onChange={(e) => setCritForm({ ...critForm, threshold_pct: e.target.value })} className="mt-1" data-testid="criterion-threshold" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!critForm.active} onCheckedChange={(v) => setCritForm({ ...critForm, active: v })} data-testid="criterion-active" />
                  Active
                </label>
                <Button onClick={handleSaveCrit} disabled={savingCrit} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="criterion-save">
                  {savingCrit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (critForm.criterion_id ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
                  {critForm.criterion_id ? 'Update criterion' : 'Add criterion'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupervisorConfig;
